import { useState, useEffect } from "react";
import API from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, CheckCircle2, AlertCircle, Calendar, Clock, MapPin, User } from "lucide-react";

const TIME_SLOTS = [
  "08:00", "08:30", "09:00", "09:30", "10:00", "10:30",
  "11:00", "11:30", "12:00", "12:30", "13:00", "13:30",
  "14:00", "14:30", "15:00", "15:30", "16:00", "16:30",
];

function formatTime(t) {
  const [h, m] = t.split(":");
  const hour = parseInt(h);
  const ampm = hour >= 12 ? "PM" : "AM";
  const h12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
  return `${h12}:${m} ${ampm}`;
}

export default function BookAppointmentDialog({ open, onOpenChange, onBooked }) {
  const [settings, setSettings] = useState(null);
  const [loadingSettings, setLoadingSettings] = useState(true);
  const [booking, setBooking] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const [practitionerId, setPractitionerId] = useState("");
  const [serviceId, setServiceId] = useState("");
  const [locationId, setLocationId] = useState("");
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");

  useEffect(() => {
    if (open && !settings) {
      API.get("/intakeq/booking-settings")
        .then(({ data }) => {
          setSettings(data);
          // Auto-select first if only one option
          if (data.practitioners?.length === 1) setPractitionerId(data.practitioners[0].Id);
          if (data.locations?.length === 1) setLocationId(data.locations[0].Id);
        })
        .catch(() => setError("Could not load booking options."))
        .finally(() => setLoadingSettings(false));
    }
  }, [open, settings]);

  const handleBook = async () => {
    if (!practitionerId || !serviceId || !locationId || !date || !time) return;
    setBooking(true);
    setError("");

    // Build UTC timestamp in milliseconds
    const localDateTime = new Date(`${date}T${time}:00`);
    const utcMs = localDateTime.getTime();

    try {
      const { data } = await API.post("/intakeq/book", {
        practitioner_id: practitionerId,
        service_id: serviceId,
        location_id: locationId,
        utc_datetime: utcMs,
        reminder_type: "Email",
      });

      if (data.success) {
        setSuccess(true);
        setTimeout(() => {
          onBooked?.();
          onOpenChange(false);
          // Reset
          setSuccess(false);
          setServiceId("");
          setDate("");
          setTime("");
        }, 2000);
      } else {
        setError(data.message || "Booking failed. Please try again.");
      }
    } catch (err) {
      setError(err?.response?.data?.detail || "Booking failed. Please try again.");
    } finally {
      setBooking(false);
    }
  };

  const selectedService = settings?.services?.find((s) => s.Id === serviceId);
  const minDate = new Date().toISOString().split("T")[0];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="book-appointment-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Book an Appointment</DialogTitle>
        </DialogHeader>

        {success ? (
          <div className="flex flex-col items-center gap-3 py-8" data-testid="booking-success">
            <CheckCircle2 className="w-12 h-12 text-green-500" />
            <p className="font-semibold text-foreground">Appointment Requested!</p>
            <p className="text-sm text-muted-foreground text-center">Your appointment request has been submitted. You'll receive a confirmation once approved.</p>
          </div>
        ) : loadingSettings ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-primary" />
          </div>
        ) : !settings?.services?.length ? (
          <div className="py-8 text-center">
            <AlertCircle className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">Could not load booking options. Your account may not be linked to IntakeQ.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Provider */}
            <div>
              <Label className="text-sm font-medium flex items-center gap-1.5 mb-1.5">
                <User className="w-3.5 h-3.5" /> Provider
              </Label>
              <Select value={practitionerId} onValueChange={setPractitionerId}>
                <SelectTrigger data-testid="select-practitioner"><SelectValue placeholder="Select provider" /></SelectTrigger>
                <SelectContent>
                  {settings.practitioners.map((p) => (
                    <SelectItem key={p.Id} value={p.Id}>{p.CompleteName}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Service */}
            <div>
              <Label className="text-sm font-medium flex items-center gap-1.5 mb-1.5">
                <Calendar className="w-3.5 h-3.5" /> Appointment Type
              </Label>
              <Select value={serviceId} onValueChange={setServiceId}>
                <SelectTrigger data-testid="select-service"><SelectValue placeholder="Select service" /></SelectTrigger>
                <SelectContent>
                  {settings.services.map((s) => (
                    <SelectItem key={s.Id} value={s.Id}>
                      {s.Name} ({s.Duration} min)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Location */}
            <div>
              <Label className="text-sm font-medium flex items-center gap-1.5 mb-1.5">
                <MapPin className="w-3.5 h-3.5" /> Location
              </Label>
              <Select value={locationId} onValueChange={setLocationId}>
                <SelectTrigger data-testid="select-location"><SelectValue placeholder="Select location" /></SelectTrigger>
                <SelectContent>
                  {settings.locations.map((l) => (
                    <SelectItem key={l.Id} value={l.Id}>{l.Name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Date & Time */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-sm font-medium mb-1.5 block">Date</Label>
                <Input
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  min={minDate}
                  className="h-11"
                  data-testid="booking-date"
                />
              </div>
              <div>
                <Label className="text-sm font-medium flex items-center gap-1.5 mb-1.5">
                  <Clock className="w-3.5 h-3.5" /> Time
                </Label>
                <Select value={time} onValueChange={setTime}>
                  <SelectTrigger data-testid="select-time"><SelectValue placeholder="Time" /></SelectTrigger>
                  <SelectContent>
                    {TIME_SLOTS.map((t) => (
                      <SelectItem key={t} value={t}>{formatTime(t)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Summary */}
            {selectedService && date && time && (
              <div className="bg-accent rounded-xl p-3 text-sm space-y-1" data-testid="booking-summary">
                <p className="font-semibold">{selectedService.Name}</p>
                <p className="text-muted-foreground">{selectedService.Duration} minutes - {new Date(date + "T00:00:00").toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })} at {formatTime(time)}</p>
              </div>
            )}

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex gap-2" data-testid="booking-error">
                <AlertCircle className="w-4 h-4 text-red-600 shrink-0 mt-0.5" />
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}

            <Button
              onClick={handleBook}
              disabled={booking || !practitionerId || !serviceId || !locationId || !date || !time}
              className="w-full h-12 rounded-xl text-base"
              data-testid="confirm-booking-button"
            >
              {booking ? <Loader2 className="w-5 h-5 animate-spin" /> : "Request Appointment"}
            </Button>

            <p className="text-[10px] text-muted-foreground text-center">
              Appointments are subject to provider confirmation. You'll receive an email notification once confirmed.
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
